import React from 'react';
import { worldCities } from '@/data/cities';
import { specialties } from '@/data/specialties';
import { cityLanguageNames, CityLanguage } from '@/context/languages/languageData';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Globe, MapPin, Layers } from 'lucide-react';

interface SitemapUrl {
  url: string;
  lastmod: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: string;
  language?: string;
  alternates?: Array<{ href: string; hreflang: string }>;
}

const MultilingualSitemapGenerator: React.FC = () => {
  const baseUrl = 'https://psychotherapist.help';
  const currentDate = new Date().toISOString().split('T')[0];
  
  // Create supported languages array from cityLanguageNames
  const supportedLanguages = Object.keys(cityLanguageNames).map(code => ({
    code: code as CityLanguage,
    name: cityLanguageNames[code as CityLanguage]
  }));

  const generateSitemapUrls = (): SitemapUrl[] => {
    const urls: SitemapUrl[] = [];

    // Main pages in all languages
    const mainPages = [
      { path: '', priority: '1.0' },
      { path: '/about', priority: '0.8' },
      { path: '/contact', priority: '0.7' },
      { path: '/find', priority: '0.9' },
      { path: '/how-it-works', priority: '0.8' },
      { path: '/pricing', priority: '0.8' },
      { path: '/specialties', priority: '0.8' },
      { path: '/for-therapists', priority: '0.8' },
      { path: '/jose-luis-marin', priority: '0.7' },
      { path: '/online-therapy', priority: '0.9' },
      { path: '/resources', priority: '0.7' }
    ];

    // Add main pages for each language
    supportedLanguages.forEach(lang => {
      mainPages.forEach(page => {
        if (lang.code === 'en') {
          // English version (default)
          urls.push({
            url: `${baseUrl}${page.path}`,
            lastmod: currentDate,
            changefreq: 'weekly',
            priority: page.priority,
            language: 'en',
            alternates: supportedLanguages.map(altLang => ({
              href: altLang.code === 'en' 
                ? `${baseUrl}${page.path}`
                : `${baseUrl}/${altLang.code}${page.path}`,
              hreflang: altLang.code
            }))
          });
        } else {
          // Other language versions
          urls.push({
            url: `${baseUrl}/${lang.code}${page.path}`,
            lastmod: currentDate,
            changefreq: 'weekly',
            priority: (parseFloat(page.priority) * 0.9).toString(),
            language: lang.code
          });
        }
      });
    });

    // Generate city pages
    worldCities.forEach(city => {
      const cityLanguage = detectCityLanguage(city);
      
      // English city page
      urls.push({
        url: `${baseUrl}/city/${city.slug}`,
        lastmod: currentDate,
        changefreq: 'daily',
        priority: '0.9',
        language: 'en',
        alternates: [
          { href: `${baseUrl}/city/${city.slug}`, hreflang: 'en' },
          ...(cityLanguage !== 'en' ? [{
            href: `${baseUrl}/${cityLanguage}/city/${city.slug}`,
            hreflang: cityLanguage
          }] : [])
        ]
      });

      // Local language city page (if different from English)
      if (cityLanguage !== 'en') {
        urls.push({
          url: `${baseUrl}/${cityLanguage}/city/${city.slug}`,
          lastmod: currentDate,
          changefreq: 'daily',
          priority: '0.95', // Higher priority for local language
          language: cityLanguage
        });
      }

      // Service-specific city pages
      specialties.forEach(service => {
        // English service page
        urls.push({
          url: `${baseUrl}/city/${city.slug}/${service.slug}`,
          lastmod: currentDate,
          changefreq: 'weekly',
          priority: '0.8',
          language: 'en',
          alternates: [
            { href: `${baseUrl}/city/${city.slug}/${service.slug}`, hreflang: 'en' },
            ...(cityLanguage !== 'en' ? [{
              href: `${baseUrl}/${cityLanguage}/city/${city.slug}/${service.slug}`,
              hreflang: cityLanguage
            }] : [])
          ]
        });

        // Local language service page
        if (cityLanguage !== 'en') {
          urls.push({
            url: `${baseUrl}/${cityLanguage}/city/${city.slug}/${service.slug}`,
            lastmod: currentDate,
            changefreq: 'weekly',
            priority: '0.85', // Higher priority for local language
            language: cityLanguage
          });
        }
      });
    });

    return urls;
  };

  const generateXmlSitemap = (): string => {
    const urls = generateSitemapUrls();
    
    let xml = `<?xml version="1.0" encoding="UTF-8"?>
`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">
`;
    
    urls.forEach(url => {
      xml += `  <url>
`;
      xml += `    <loc>${url.url}</loc>
`;
      xml += `    <lastmod>${url.lastmod}</lastmod>
`;
      xml += `    <changefreq>${url.changefreq}</changefreq>
`;
      xml += `    <priority>${url.priority}</priority>
`;
      
      // Add hreflang alternates
      if (url.alternates) {
        url.alternates.forEach(alt => {
          xml += `    <xhtml:link rel="alternate" hreflang="${alt.hreflang}" href="${alt.href}" />
`;
        });
      }
      
      xml += `  </url>
`;
    });
    
    xml += `</urlset>`;
    return xml;
  };

  const generateLanguageSitemap = (): string => {
    const urls = generateSitemapUrls();
    const languageStats: Record<string, number> = {};
    
    urls.forEach(url => {
      if (url.language) {
        languageStats[url.language] = (languageStats[url.language] || 0) + 1;
      }
    });

    let xml = `<?xml version="1.0" encoding="UTF-8"?>
`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
`;
    
    Object.entries(languageStats).forEach(([lang, count]) => {
      xml += `  <!-- ${lang.toUpperCase()}: ${count} URLs -->
`;
    });
    
    supportedLanguages.forEach(lang => {
      const langUrls = urls.filter(u => u.language === lang.code);
      langUrls.forEach(url => {
        xml += `  <url>
`;
        xml += `    <loc>${url.url}</loc>
`;
        xml += `    <lastmod>${url.lastmod}</lastmod>
`;
        xml += `    <changefreq>${url.changefreq}</changefreq>
`;
        xml += `    <priority>${url.priority}</priority>
`;
        xml += `  </url>
`;
      });
    });
    
    xml += `</urlset>`;
    return xml;
  };

  const downloadSitemap = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const urls = generateSitemapUrls();
  const totalUrls = urls.length;
  const languageBreakdown = urls.reduce((acc, url) => {
    if (url.language) {
      acc[url.language] = (acc[url.language] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const cityCount = worldCities.length;
  const serviceCount = specialties.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Multilingual Sitemap Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-primary/5 rounded-lg">
              <div className="text-2xl font-bold text-primary">{totalUrls.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Total URLs</div>
            </div>
            <div className="text-center p-4 bg-secondary/5 rounded-lg">
              <div className="text-2xl font-bold text-secondary">{cityCount.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Cities</div>
            </div>
            <div className="text-center p-4 bg-accent/5 rounded-lg">
              <div className="text-2xl font-bold text-accent">{serviceCount}</div>
              <div className="text-sm text-muted-foreground">Services</div>
            </div>
            <div className="text-center p-4 bg-muted/5 rounded-lg">
              <div className="text-2xl font-bold">{supportedLanguages.length}</div>
              <div className="text-sm text-muted-foreground">Languages</div>
            </div>
          </div>

          {/* Language Breakdown */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Layers className="h-4 w-4" />
              URLs by Language
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {Object.entries(languageBreakdown)
                .sort(([,a], [,b]) => b - a)
                .map(([lang, count]) => (
                  <div key={lang} className="text-center p-2 bg-muted/20 rounded">
                    <div className="text-sm font-mono text-primary">{lang.toUpperCase()}</div>
                    <div className="text-xs text-muted-foreground">{count.toLocaleString()}</div>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Download Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => downloadSitemap(generateXmlSitemap(), 'sitemap.xml')}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download Complete Sitemap
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => downloadSitemap(generateLanguageSitemap(), 'sitemap-by-language.xml')}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download Language Sitemap
            </Button>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Sample URLs Preview
            </h3>
            <div className="bg-muted/20 rounded-lg p-4 font-mono text-sm space-y-1 max-h-40 overflow-y-auto">
              {urls.slice(0, 10).map((url, index) => (
                <div key={index} className="text-xs">
                  <span className="text-primary">{url.language}</span>: {url.url}
                </div>
              ))}
              {urls.length > 10 && (
                <div className="text-xs text-muted-foreground">
                  ... and {(urls.length - 10).toLocaleString()} more URLs
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MultilingualSitemapGenerator;
